import React, { useCallback } from "react";
import i18next, { TFunction } from "i18next";
import { initReactI18next, Trans, useTranslation as useI18nTranslation } from "react-i18next";
import en from "./en";
import ru from "./ru";

type InterpolationMap = Record<string, string | number | boolean>;

interface I18nProps {
  path: string;
  components?: Record<string, React.ReactNode>;
  values?: Record<string, any>;
}

export type AppLanguage = "ru" | "en";
export const SUPPORTED_LANGUAGES: AppLanguage[] = ["ru", "en"];
const LANG_STORAGE_KEY = "scrile_lang";
const DEFAULT_LANGUAGE: AppLanguage = "ru";

function readStoredLanguage(): AppLanguage {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === "ru" || stored === "en") return stored;
  } catch {
    // localStorage may be unavailable (SSR, privacy mode) — fall through to default
  }
  return DEFAULT_LANGUAGE;
}

let tFunction: TFunction;
i18next
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources: {
      en: {
        translation: {
          ...en,
        },
      },
      ru: {
        translation: {
          ...ru,
        },
      },
    },
    lng: readStoredLanguage(),
    fallbackLng: "en",
    // We use the English sentence itself as the translation key (e.g. t("1. Client records & uploads")).
    // The defaults treat "." as a nested-path separator and ":" as a namespace separator, which
    // breaks any key containing those characters — disable both so keys are matched as literal strings.
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false,
    },
  })
  .then((t) => (tFunction = t));

export const t = (key: string, interpolationMap?: InterpolationMap) => {
  if (!tFunction) return key;
  return tFunction(key, interpolationMap);
};

export function useTranslation() {
  const { t } = useI18nTranslation();

  return { t: useCallback((key: string, interpolationMap?: InterpolationMap) => t(key, interpolationMap), [t]) };
}

export function I18n({ path, components, values }: I18nProps) {
  return <Trans i18nKey={path} components={components} values={values} />;
}

export function getCurrentLanguage(): AppLanguage {
  const current = i18next.language as AppLanguage | undefined;
  if (current === "ru" || current === "en") return current;
  return DEFAULT_LANGUAGE;
}

export function setLanguage(lang: AppLanguage) {
  if (!SUPPORTED_LANGUAGES.includes(lang)) return;
  i18next.changeLanguage(lang);
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // ignore storage errors — language still applies in-memory
  }
  // Keep <html lang> in sync — important for accessibility and screen readers.
  try {
    document.documentElement.setAttribute("lang", lang);
  } catch {
    // ignore (SSR / no DOM)
  }
}

// Apply <html lang> immediately on module load so the very first paint is correct.
try {
  document.documentElement.setAttribute("lang", readStoredLanguage());
} catch {
  // ignore (SSR / no DOM)
}
